import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Text, Spinner, Badge, Button, MessageBar, MessageBarBody,
  Divider, makeStyles, tokens, Avatar,
} from '@fluentui/react-components'
import { Edit24Regular, BuildingBank24Regular, ClipboardTask24Regular } from '@fluentui/react-icons'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { meService, type MyPensionContribution } from '../services/meService'
import { useAuth } from '../auth/useAuth'
import { getEntraUserPhoto } from '../services/graphService'

const IS_MOCK = import.meta.env.VITE_MOCK_AUTH === 'true'
import { FrequencyLabels, ContributionTypeLabels, type ContributionFrequency, type ContributionType } from '../services/pensionContributionsService'
import { documentDefinitionsService, type EmployeeDocumentRecord, type PersonDocumentRecord } from '../services/documentDefinitionsService'
import VacationBalanceSummary from '../components/VacationBalanceSummary'

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  header:      { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
  card:        { backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusMedium, padding: '24px', boxShadow: tokens.shadow4 },
  infoGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 32px', marginTop: '16px' },
  infoGrid3:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px 32px', marginTop: '16px' },
  infoLabel:   {
    fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' as const,
    letterSpacing: '.07em', color: tokens.colorNeutralForeground3, marginBottom: '2px',
  },
  infoValue:   { fontSize: '14px', fontWeight: 600, color: tokens.colorNeutralForeground1, lineHeight: '1.4' },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    paddingBottom: '10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  subsection: {
    fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' as const,
    letterSpacing: '.06em', color: tokens.colorNeutralForeground2,
    marginTop: '20px', paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralBackground3}`,
  },
  balRow:  { display: 'flex', gap: '24px', flexWrap: 'wrap', margin: '16px 0' },
  stat:    { display: 'flex', flexDirection: 'column' as const, minWidth: '80px' },
  statVal: { fontSize: '28px', fontWeight: 700, lineHeight: 1.1 },
  statLbl: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
})

const EMPLOYMENT_LABELS: Record<number, string> = {
  1: 'HPP – Hlavní pracovní poměr',
  2: 'DPP – Dohoda o provedení práce',
  3: 'DPC – Dohoda o pracovní činnosti',
}

const CONTRACT_LABELS: Record<number, string> = {
  1: 'Na dobu neurčitou',
  2: 'Na dobu určitou',
  3: 'DPP', 4: 'DPČ',
}


const DAY_LABELS: Record<string, string> = { mon: 'Po', tue: 'Út', wed: 'St', thu: 'Čt', fri: 'Pá' }

function getRoleLabel(auth: ReturnType<typeof useAuth>, personType: string): string {
  if (auth.isAdmin) return 'Admin'
  if (auth.isHR) return 'HR'
  if (auth.isManager) return 'Manažer'
  if (personType === 'contractor') return 'Externista'
  return 'Zaměstnanec'
}

const fmt = (date: string | null | undefined) =>
  date ? new Date(date).toLocaleDateString('cs-CZ') : null

// ── InfoField ─────────────────────────────────────────────────────────────────

function InfoField({ label, value, children, span }: {
  label: string
  value?: string | null
  children?: React.ReactNode
  span?: boolean
}) {
  const styles = useStyles()
  const content = children ?? value
  if (content === null || content === undefined || content === '') return null
  return (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <div className={styles.infoLabel}>{label}</div>
      <div className={styles.infoValue}>{content}</div>
    </div>
  )
}

// ── WorkScheduleCard ──────────────────────────────────────────────────────────

function WorkScheduleCard({ workScheduleJson, weeklyHours }: {
  workScheduleJson: string | null
  weeklyHours: number
}) {
  const styles = useStyles()
  const days = ['mon', 'tue', 'wed', 'thu', 'fri']
  let schedule: Record<string, number> | null = null
  if (workScheduleJson) {
    try { schedule = JSON.parse(workScheduleJson) } catch { /* ignore */ }
  }
  const weekly = schedule
    ? days.reduce((s, d) => s + (schedule![d] ?? 0), 0)
    : weeklyHours

  return (
    <div className={styles.card} style={{ marginTop: 24 }}>
      <Text size={500} weight="semibold">Pracovní rozvrh</Text>
      <Divider style={{ margin: '12px 0' }} />
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className={styles.stat}>
          <span className={styles.statVal}>{Math.round(weekly / 40 * 100)} %</span>
          <span className={styles.statLbl}>Úvazek</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statVal}>{weekly.toFixed(weekly % 1 === 0 ? 0 : 1)}</span>
          <span className={styles.statLbl}>Hod / týden</span>
        </div>
        {schedule && (
          <div style={{ display: 'flex', gap: 16 }}>
            {days.map(d => (
              <div key={d} className={styles.stat} style={{ minWidth: 36, textAlign: 'center' }}>
                <span style={{ fontWeight: 600 }}>{schedule![d] ?? 0} h</span>
                <span className={styles.statLbl}>{DAY_LABELS[d]}</span>
              </div>
            ))}
          </div>
        )}
        {!schedule && weekly === 40 && (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Standardní rozvrh: Po–Pá 8 h</Text>
        )}
      </div>
    </div>
  )
}

// ── PensionCard ───────────────────────────────────────────────────────────────

function PensionCard() {
  const styles = useStyles()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['me', 'pension'],
    queryFn: meService.getMyPension,
  })

  if (isLoading) return null
  if (isError || !data) return null

  const active = data as MyPensionContribution

  return (
    <div className={styles.card} style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <BuildingBank24Regular />
        <Text size={500} weight="semibold">Příspěvek na stáří</Text>
      </div>
      <Divider style={{ marginBottom: 12 }} />
      <div className={styles.infoGrid}>
        <InfoField label="Penzijní ústav"  value={active.pensionProvider} />
        <InfoField label="Výše příspěvku">
          <span style={{ fontWeight: 700 }}>{active.amount.toLocaleString('cs-CZ')} Kč</span>
        </InfoField>
        <InfoField label="Frekvence" value={FrequencyLabels[active.frequency as ContributionFrequency]} />
        <InfoField label="Typ"       value={ContributionTypeLabels[active.contributionType as ContributionType]} />
        {active.validFrom && (
          <InfoField label="Platnost od" value={active.validFrom.slice(0, 10).split('-').reverse().join('.')} />
        )}
      </div>
    </div>
  )
}

// ── DocumentsCard ─────────────────────────────────────────────────────────────

function DocumentsCard({ employeeId }: { employeeId: number }) {
  const queryClient = useQueryClient()
  const queryKey = ['employee-documents', employeeId]
  const { data = [] } = useQuery({
    queryKey,
    queryFn: () => documentDefinitionsService.getEmployeeDocuments(employeeId),
  })
  const completeMutation = useMutation({
    mutationFn: (docId: number) => documentDefinitionsService.completeDocument(docId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  // Zobrazujeme Seznámení (2) a Školení LMS (3) — Podpis (1) potvrzuje HR
  const docs = data.filter((d: EmployeeDocumentRecord) => d.documentType === 2 || d.documentType === 3)
  if (docs.length === 0) return null

  const pending = docs.filter((d: EmployeeDocumentRecord) => !d.isCompleted)

  return (
    <div style={{ marginTop: 24, backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusMedium, padding: '24px', boxShadow: tokens.shadow4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <ClipboardTask24Regular />
        <Text size={500} weight="semibold">Moje dokumenty</Text>
        {pending.length > 0 && <Badge color="danger">{pending.length} čeká</Badge>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {docs.map((doc: EmployeeDocumentRecord) => {
          const overdue = !doc.isCompleted && doc.deadlineDate && new Date(doc.deadlineDate) < new Date()
          return (
            <div key={doc.id} style={{
              padding: '12px', borderRadius: '6px',
              background: doc.isCompleted ? tokens.colorNeutralBackground2 : overdue ? tokens.colorPaletteRedBackground1 : tokens.colorNeutralBackground3,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {doc.documentUrl
                    ? <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1 }}>{doc.documentName}</a>
                    : doc.documentName}
                </div>
                <Badge color={doc.isCompleted ? 'success' : overdue ? 'danger' : 'informative'}>
                  {doc.isCompleted ? 'Splněno' : overdue ? 'Po termínu' : 'Čeká'}
                </Badge>
              </div>
              {doc.deadlineDate && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  Termín: {new Date(doc.deadlineDate).toLocaleDateString('cs-CZ')}
                </Text>
              )}
              {!doc.isCompleted && doc.documentType === 2 && (
                <Button size="small" appearance="primary" style={{ alignSelf: 'flex-start', marginTop: 4 }}
                  disabled={completeMutation.isPending}
                  onClick={() => completeMutation.mutate(doc.id)}>
                  Potvrdit seznámení
                </Button>
              )}
              {!doc.isCompleted && doc.documentType === 3 && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: 4 }}>
                  Výsledek bude importován z LMS systému
                </Text>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── ContractorDocumentsCard ───────────────────────────────────────────────────

function ContractorDocumentsCard({ contractorId }: { contractorId: number }) {
  const queryClient = useQueryClient()
  const queryKey = ['contractor-documents', contractorId]
  const { data = [] } = useQuery({
    queryKey,
    queryFn: () => documentDefinitionsService.getContractorDocuments(contractorId),
  })
  const completeMutation = useMutation({
    mutationFn: (docId: number) => documentDefinitionsService.completeDocument(docId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  const docs = (data as PersonDocumentRecord[]).filter(d => d.documentType === 2 || d.documentType === 3)
  if (docs.length === 0) return null

  const pending = docs.filter(d => !d.isCompleted)

  return (
    <div style={{ marginTop: 24, backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusMedium, padding: '24px', boxShadow: tokens.shadow4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <ClipboardTask24Regular />
        <Text size={500} weight="semibold">Moje dokumenty</Text>
        {pending.length > 0 && <Badge color="danger">{pending.length} čeká</Badge>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {docs.map(doc => {
          const overdue = !doc.isCompleted && doc.deadlineDate && new Date(doc.deadlineDate) < new Date()
          return (
            <div key={doc.id} style={{
              padding: '12px', borderRadius: '6px',
              background: doc.isCompleted ? tokens.colorNeutralBackground2 : overdue ? tokens.colorPaletteRedBackground1 : tokens.colorNeutralBackground3,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {doc.documentUrl
                    ? <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1 }}>{doc.documentName}</a>
                    : doc.documentName}
                </div>
                <Badge color={doc.isCompleted ? 'success' : overdue ? 'danger' : 'informative'}>
                  {doc.isCompleted ? 'Splněno' : overdue ? 'Po termínu' : 'Čeká'}
                </Badge>
              </div>
              {doc.deadlineDate && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  Termín: {new Date(doc.deadlineDate).toLocaleDateString('cs-CZ')}
                </Text>
              )}
              {!doc.isCompleted && doc.documentType === 2 && (
                <Button size="small" appearance="primary" style={{ alignSelf: 'flex-start', marginTop: 4 }}
                  disabled={completeMutation.isPending}
                  onClick={() => completeMutation.mutate(doc.id)}>
                  Potvrdit seznámení
                </Button>
              )}
              {!doc.isCompleted && doc.documentType === 3 && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: 4 }}>
                  Výsledek bude importován z LMS systému
                </Text>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── ProfileHeader ─────────────────────────────────────────────────────────────

function ProfileHeader({ profile }: {
  profile: import('../services/meService').MyProfile
}) {
  const styles = useStyles()
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!profile.entraObjectId || IS_MOCK) return
    let objectUrl: string | null = null
    getEntraUserPhoto(profile.entraObjectId).then(url => {
      objectUrl = url
      setPhotoUrl(url)
    })
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [profile.entraObjectId])

  return (
    <div className={styles.header}>
      <Avatar
        size={48}
        name={profile.fullName}
        image={photoUrl ? { src: photoUrl } : undefined}
        style={{ flexShrink: 0 }}
      />
      <div style={{ flex: 1 }}>
        <Text size={600} weight="semibold">{profile.fullName}</Text>
      </div>
    </div>
  )
}

// ── Hlavní komponenta ─────────────────────────────────────────────────────────

export default function MyProfilePage() {
  const styles = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const auth = useAuth()

  const { data: me, isLoading, isError, error } = useQuery({
    queryKey: ['me'],
    queryFn: meService.getMe,
    retry: false,
  })
  const [showOther, setShowOther] = useState(false)

  if (isLoading) return <Spinner label={t('app.loading')} />

  if (isError) {
    const detail = (error as any)?.response?.data
    return (
      <div>
        <Text size={600} weight="semibold" block style={{ marginBottom: '16px' }}>{t('nav.myProfile')}</Text>
        <MessageBar intent="warning" style={{ maxWidth: 560 }}>
          <MessageBarBody>
            <Text weight="semibold" block>Váš profil zatím není v systému.</Text>
            {detail?.email && (
              <Text block style={{ marginTop: 4 }}>Přihlašovací e-mail: <strong>{detail.email}</strong></Text>
            )}
            <Text block style={{ marginTop: 8 }}>
              Požádejte HR nebo administrátora, aby vás přidal do evidence zaměstnanců
              se stejnou e-mailovou adresou, jakou používáte pro přihlášení.
            </Text>
          </MessageBarBody>
        </MessageBar>
      </div>
    )
  }

  if (!me) return null

  // Přepínač rolí: když je člověk současně zaměstnanec i externista, zobrazíme vybranou identitu
  const other = me.otherRole ?? null
  const profile = showOther && other ? other : me

  const isEmployee   = profile.personType === 'employee'
  const isContractor = profile.personType === 'contractor'
  const hasAddress = profile.address || profile.city || profile.postalCode
  const bankAccount = profile.bankAccount
  const hasChildren = [1, 2, 3, 4, 5].some(n => profile[`child${n}Name` as keyof typeof profile])
  const roleLabel = getRoleLabel(auth, profile.personType)

  return (
    <div>
      {other && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Button
            size="small"
            appearance={!showOther ? 'primary' : 'secondary'}
            onClick={() => setShowOther(false)}
          >
            {me.personType === 'contractor' ? 'Externista' : 'Zaměstnanec'}
          </Button>
          <Button
            size="small"
            appearance={showOther ? 'primary' : 'secondary'}
            onClick={() => setShowOther(true)}
          >
            {other.personType === 'contractor' ? 'Externista' : 'Zaměstnanec'}
          </Button>
        </div>
      )}

      {/* Záhlaví */}
      <ProfileHeader profile={profile} />


      {/* ── Karta: Základní údaje (zaměstnanec) ── */}
      {isEmployee && (
        <div className={styles.card}>
          <Text className={styles.sectionTitle}>Základní údaje</Text>

          <div className={styles.infoGrid}>
            <InfoField label="Role v People">
              <Badge color="informative">{roleLabel}</Badge>
            </InfoField>

            <InfoField label="Datum nástupu" value={fmt(profile.hireDate)} />

            {profile.cooperationStartDate && (() => {
              const start = new Date(profile.cooperationStartDate)
              const today = new Date()
              let years = today.getFullYear() - start.getFullYear()
              const m = today.getMonth() - start.getMonth()
              if (m < 0 || (m === 0 && today.getDate() < start.getDate())) years--
              return years >= 0 ? (
                <>
                  <InfoField label="Zahájení spolupráce" value={fmt(profile.cooperationStartDate)} />
                  <InfoField label="Délka spolupráce" value={`${years} ${years === 1 ? 'rok' : years <= 4 ? 'roky' : 'let'}`} />
                </>
              ) : null
            })()}

            <InfoField label="Typ pracovního poměru" value={EMPLOYMENT_LABELS[profile.employmentType] ?? String(profile.employmentType)} />
            <InfoField label="Typ smlouvy"           value={CONTRACT_LABELS[profile.contractType]    ?? String(profile.contractType)} />
            {profile.contractType === 2 && (
              <InfoField label="Datum ukončení smlouvy" value={fmt(profile.contractEndDate)} />
            )}
            <InfoField label="Ukončení zkušební doby" value={fmt(profile.probationEndDate)} />
            {profile.terminationDate && (
              <InfoField label="Datum ukončení" value={fmt(profile.terminationDate)} />
            )}
          </div>

          <div className={styles.subsection}>Pracovní zařazení</div>
          <div className={styles.infoGrid}>
            <InfoField label="Oddělení"     value={profile.departmentName} />
            <InfoField label="Pozice"       value={profile.positionName} />
            <InfoField label="Nadřízený"    value={profile.managerFullName} />
            <InfoField label="Středisko"    value={profile.costCenter} />
            <InfoField label="Osobní číslo" value={profile.personalNumber} />
            <InfoField label="Stravenky"    value={profile.hasMealVouchers ? 'Ano' : 'Ne'} />
          </div>

          <div className={styles.subsection}>Pracovní kontakt</div>
          <div className={styles.infoGrid}>
            <InfoField label="E-mail">
              {profile.email
                ? <a href={`mailto:${profile.email}`} style={{ color: tokens.colorBrandForeground1 }}>{profile.email}</a>
                : null}
            </InfoField>
            <InfoField label="Telefon" value={profile.phone} />
          </div>
        </div>
      )}

      {/* ── Karta: Základní údaje (externista) ── */}
      {isContractor && (
        <div className={styles.card}>
          <Text className={styles.sectionTitle}>Základní údaje</Text>

          <div className={styles.infoGrid}>
            <InfoField label="Role v People">
              <Badge color="informative">{roleLabel}</Badge>
            </InfoField>
            <InfoField label="Firma" value={profile.companyName} />
            <InfoField label="IČO"   value={profile.ico} />
            <InfoField label="Plátce DPH" value={profile.isVatPayer ? 'Ano' : 'Ne'} />
          </div>

          <div className={styles.subsection}>Kontaktní osoba</div>
          <div className={styles.infoGrid}>
            <InfoField label="Jméno"    value={`${profile.fullName}`} />
            <InfoField label="ORBIT e-mail">
              {profile.email
                ? <a href={`mailto:${profile.email}`} style={{ color: tokens.colorBrandForeground1 }}>{profile.email}</a>
                : null}
            </InfoField>
            <InfoField label="Telefon" value={profile.phone} />
            {profile.cooperationStartDate && (() => {
              const start = new Date(profile.cooperationStartDate)
              const today = new Date()
              let years = today.getFullYear() - start.getFullYear()
              const m = today.getMonth() - start.getMonth()
              if (m < 0 || (m === 0 && today.getDate() < start.getDate())) years--
              return years >= 0 ? (
                <>
                  <InfoField label="Zahájení spolupráce" value={fmt(profile.cooperationStartDate)} />
                  <InfoField label="Délka spolupráce" value={`${years} ${years === 1 ? 'rok' : years <= 4 ? 'roky' : 'let'}`} />
                </>
              ) : null
            })()}
          </div>

          <div className={styles.subsection}>Smlouva</div>
          <div className={styles.infoGrid}>
            <InfoField label="Začátek platnosti smlouvy" value={fmt(profile.hireDate)} />
            <InfoField label="Konec smlouvy"             value={fmt(profile.contractEndDate) ?? 'Neurčito'} />
            <InfoField label="Předpokládané činnosti"    value={profile.expectedActivities} />
            <InfoField label="Oddělení"                  value={profile.departmentName} />
            <InfoField label="Odpovědný manažer"         value={profile.managerFullName} />
          </div>
        </div>
      )}

      {/* ── Dokumenty (externista) ── */}
      {isContractor && profile.contractorId && (
        <ContractorDocumentsCard contractorId={profile.contractorId} />
      )}

      {/* ── Karta: Soukromé údaje ── */}
      {isEmployee && (
        <div className={styles.card} style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
            <Text className={styles.sectionTitle} style={{ border: 'none', paddingBottom: 0 }}>Soukromé údaje</Text>
            <Button size="small" appearance="subtle" icon={<Edit24Regular />} onClick={() => navigate('/my-profile/edit')}>
              Upravit
            </Button>
          </div>

          <div className={styles.infoGrid}>
            <InfoField label="Datum narození"      value={fmt(profile.birthDate)} />
            <InfoField label="Rodné číslo"         value={profile.personalId} />
            <InfoField label="Zdravotní pojišťovna" value={profile.healthInsurance} />
            <InfoField label="Kód pojišťovny"      value={profile.healthInsuranceCode} />
            <InfoField label="Osobní e-mail">
              {profile.personalEmail
                ? <a href={`mailto:${profile.personalEmail}`} style={{ color: tokens.colorBrandForeground1 }}>{profile.personalEmail}</a>
                : null}
            </InfoField>
            <InfoField label="Osobní telefon"  value={profile.personalPhone} />
          </div>

          {(profile.nextOfKinName || profile.nextOfKinAddress || profile.nextOfKinEmail || profile.nextOfKinPhone) && (
            <>
              <div className={styles.subsection}>Osoba blízká</div>
              <div className={styles.infoGrid}>
                <InfoField label="Jméno"    value={profile.nextOfKinName} />
                <InfoField label="Adresa"   value={profile.nextOfKinAddress} />
                <InfoField label="E-mail"   value={profile.nextOfKinEmail} />
                <InfoField label="Telefon"  value={profile.nextOfKinPhone} />
              </div>
            </>
          )}

          {hasAddress && (
            <>
              <div className={styles.subsection}>Adresa</div>
              <div className={styles.infoGrid3}>
                <InfoField label="Ulice a číslo" value={profile.address} />
                <InfoField label="Obec"           value={profile.city} />
                <InfoField label="PSČ"            value={profile.postalCode} />
                <InfoField label="Stát"           value={profile.country} />
              </div>
            </>
          )}

          {bankAccount && (
            <>
              <div className={styles.subsection}>Bankovní spojení</div>
              <div className={styles.infoGrid}>
                <InfoField label="Číslo účtu" value={bankAccount} />
              </div>
            </>
          )}

          {profile.vehicle && (
            <>
              <div className={styles.subsection}>Ostatní</div>
              <div className={styles.infoGrid}>
                <InfoField label="Vozidlo" value={profile.vehicle} />
              </div>
            </>
          )}

          {hasChildren && (
            <>
              <div className={styles.subsection}>Děti</div>
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[1, 2, 3, 4, 5].filter(n => profile[`child${n}Name` as keyof typeof profile]).map(n => {
                  const name       = profile[`child${n}Name`       as keyof typeof profile] as string
                  const personalId = profile[`child${n}PersonalId` as keyof typeof profile] as string | undefined
                  const studies    = profile[`child${n}Studies`    as keyof typeof profile] as boolean | undefined
                  return (
                    <div key={n} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 32px' }}>
                      <div>
                        <div className={styles.infoLabel}>Jméno</div>
                        <div className={styles.infoValue}>{name}</div>
                      </div>
                      <div>
                        {personalId ? (
                          <>
                            <div className={styles.infoLabel}>Rodné číslo</div>
                            <div className={styles.infoValue}>{personalId}</div>
                          </>
                        ) : null}
                      </div>
                      <div>
                        {studies ? (
                          <>
                            <div className={styles.infoLabel}>Studuje</div>
                            <div className={styles.infoValue}>Ano</div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {profile.note && (
            <>
              <div className={styles.subsection}>Poznámka</div>
              <div style={{ fontSize: 14, color: tokens.colorNeutralForeground2, fontStyle: 'italic', marginTop: 8, lineHeight: 1.5 }}>
                {profile.note}
              </div>
            </>
          )}
        </div>
      )}

      {/* Pracovní rozvrh */}
      {isEmployee && (
        <WorkScheduleCard
          workScheduleJson={profile.workScheduleJson}
          weeklyHours={profile.weeklyHours ?? 40}
        />
      )}

      {/* Dovolená — pouze zaměstnanci */}
      {isEmployee && (
        <div style={{ marginTop: 24 }}>
          <VacationBalanceSummary />
        </div>
      )}

      {/* Penzijko — pouze zaměstnanci */}
      {isEmployee && <PensionCard />}

      {/* Dokumenty — pouze zaměstnanci */}
      {isEmployee && profile.id && <DocumentsCard employeeId={profile.id} />}
    </div>
  )
}
